import GachaTicketDiscount from 'components/GachaTicketDiscount';
import {
  CardPayment,
  PaymentAmount,
  Header,
  PageWrapper,
  Text,
  ModalPaymentSuccess,
  DeferralPaymentRequest,
} from 'components';
import {
  LIMIT_VALUE,
  MESSAGE_KEY,
  TEXT_STRING,
  queryKeys,
  routes,
} from '../../constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPendingPayment, payMoney } from 'api';
import {
  EDeferralRequestType,
  EPaymentFor,
  EPaymentMethod,
  TMoneyPaymentByCardBody,
  TPaymentPayload,
} from 'types';
import { useMessage, useToast } from 'hooks';
import { useUserStore } from 'store';
import MethodPayment from 'components/PaymentMethod/PaymentMethod';
import { formatAmountNumber, getFormattedAmount, pushDataLayer } from 'utils';
import { usePubNub } from 'pubnub-react';

enum EPaymentStep {
  CHOOSE_METHOD = 1,
  ENTER_AMOUNT = 2,
  PAYMENT = 3,
  DEFERRAL_PAYMENT_REQUEST = 4,
}

const paymentStr = TEXT_STRING.PAYMENT;
const commonStr = TEXT_STRING.COMMON;

const Payment = () => {
  // Hooks
  const { id: paymentId } = useParams();

  const navigate = useNavigate();
  const { toastError } = useToast();
  const { user, setUserRefreshRequested } = useUserStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const pubnub = usePubNub();
  const { sendMessage } = useMessage({ pubnub });
  const [searchParams] = useSearchParams();
  const isPostpayParam = searchParams.has(MESSAGE_KEY.POST_PAY);
  const isBankParam = searchParams.has(MESSAGE_KEY.BANK_PAY);

  // States
  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethod>(
    EPaymentMethod.CASH,
  );
  const [stepPayment, setStepPayment] = useState<EPaymentStep>(
    EPaymentStep.CHOOSE_METHOD,
  );
  const [amountPayment, setAmountPayment] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);

  // Query
  const { data, isLoading, isRefetching, error, isError, refetch } = useQuery({
    queryKey: [queryKeys.PAYMENT, paymentId],
    queryFn: () => getPendingPayment(paymentId || ''),
    enabled: !!paymentId,
    retry: 0,
  });

  // Mutation
  const {
    mutateAsync,
    isPending,
    data: resPayment,
  } = useMutation({
    mutationFn: (body: TMoneyPaymentByCardBody) => {
      return payMoney(body);
    },
    onSuccess(data) {
      if (data?.tds_auth_url) {
        window.open(data?.tds_auth_url, '_self');
      } else {
        setOpen(true);
      }
      if (data?.name && data?.channel) {
        sendMessage({
          name: data?.name,
          image: data?.image,
          animations: data?.animations,
          message: '',
          channel: data?.channel,
          female: data?.female,
        });
      }

      if (data?.isFinished) {
        if (data.isFirst) {
          pushDataLayer({ event: 'first_paid', label: `${user?.id || ''}` });
        } else {
          pushDataLayer({ event: 'repeat_paid', label: `${user?.id || ''}` });
        }
        queryClient.refetchQueries({
          queryKey: [queryKeys.LIST_PENDING_PAYMENT],
        });
      } else {
        refetch();
      }
    },
    onError(err: any) {
      if (!err?.response?.data?.retry && !isPayForGift) {
        refetch();
        setStepPayment(EPaymentStep.CHOOSE_METHOD);
        setAmountPayment(0);
      }
    },
  });

  // Memo, callback
  const isDeferralPayment = useMemo(
    () =>
      isPostpayParam ||
      isBankParam ||
      stepPayment === EPaymentStep.DEFERRAL_PAYMENT_REQUEST,
    [isPostpayParam, isBankParam, stepPayment],
  );

  const deferralType: EDeferralRequestType = useMemo(() => {
    if (paymentMethod === EPaymentMethod.POSTPAY || isPostpayParam) {
      return EDeferralRequestType.POSTPAY;
    }
    return EDeferralRequestType.BANK;
  }, [paymentMethod, isPostpayParam]);

  const isPayForGift = useMemo(
    () => data?.payFor === EPaymentFor.GIFT,
    [data?.payFor],
  );

  const allowPaymentMethod: EPaymentMethod[] | undefined = useMemo(() => {
    return data?.allowPaymentMethod;
  }, [data?.allowPaymentMethod]);

  const totalAmount = useMemo(() => {
    if (!data) return 0;
    if (
      paymentMethod === EPaymentMethod.CARD ||
      paymentMethod === EPaymentMethod.VISA
    ) {
      return +data.amount * (1 + data.fee);
    }
    return +data.amount;
  }, [data, paymentMethod]);

  const handlePayment = useCallback(
    async (payload?: TPaymentPayload) => {
      const { token, customerId, type } = payload || {};
      if (
        type === EPaymentMethod.VISA &&
        amountPayment > LIMIT_VALUE.MAX_PAYMENT_VISA &&
        !isPayForGift
      ) {
        return toastError(
          new Error(
            TEXT_STRING.ERROR.LIMIT_NUMBER.replace(
              '$number',
              formatAmountNumber(LIMIT_VALUE.MAX_PAYMENT_VISA),
            ),
          ),
        );
      }
      if (data?.id && !!paymentMethod && !isPending) {
        await mutateAsync({
          paymentId: data?.id,
          token,
          customerId,
          type: type || paymentMethod,
          payFor: data?.payFor,
          amount: amountPayment,
          projectId: data?.projectId,
          allowMethods: data?.allowPaymentMethod,
          secureReturnUrl:
            type === EPaymentMethod.VISA
              ? `${window.location.origin}${routes.FINISH_PAYMENT.replace(
                  ':id',
                  data?.id,
                )}?redirect=${encodeURIComponent(
                  routes.CONVERSATION.replace(
                    ':id',
                    location?.state?.channelId,
                  ),
                )}`
              : '',
        });
      }
    },
    [
      amountPayment,
      isPayForGift,
      data?.id,
      data?.payFor,
      data?.projectId,
      data?.allowPaymentMethod,
      paymentMethod,
      isPending,
      toastError,
      mutateAsync,
      location?.state?.channelId,
    ],
  );

  const handleChangeAmount = useCallback(
    (amount: number) => {
      if (amount >= 0) {
        const maxAmount = +(data?.amount ?? 0);
        setAmountPayment(amount > maxAmount ? maxAmount : amount);
      }
    },
    [data?.amount],
  );

  const handleMethodPayment = useCallback(async () => {
    try {
      if (
        [
          EPaymentMethod.CASH,
          EPaymentMethod.POINT,
          EPaymentMethod.POSTPAY,
          EPaymentMethod.BANK,
        ].includes(paymentMethod)
      ) {
        await handlePayment();
      } else {
        setStepPayment(EPaymentStep.PAYMENT);
      }
    } catch (error) {
      toastError(error as Error);
    }
  }, [paymentMethod, handlePayment, toastError]);

  const handleSetStepPayment = useCallback(() => {
    setPaymentMethod(EPaymentMethod.CARD);
    setStepPayment(EPaymentStep.PAYMENT);
    setAmountPayment(data?.amount ?? 0);
  }, [data?.amount]);

  const onConfirmSuccess = useCallback(() => {
    const navigateMessage = () =>
      navigate(
        routes.CONVERSATION.replace(':id', resPayment?.channelId || ''),
        {
          state: {
            isFinish: true,
          },
          replace: true,
        },
      );
    if (!resPayment?.isFinished && isPayForGift) {
      navigateMessage();
    }

    if (resPayment?.isFinished) {
      queryClient.refetchQueries({ queryKey: [queryKeys.CURRENT_USER] });
      resPayment?.channelId ? navigateMessage() : navigate(-1);
    } else {
      setStepPayment(EPaymentStep.CHOOSE_METHOD);
    }
    setAmountPayment(0);
    setOpen(false);
  }, [
    isPayForGift,
    navigate,
    queryClient,
    resPayment?.channelId,
    resPayment?.isFinished,
  ]);

  const handleBack = useCallback(() => {
    if (data?.isPaymentRequest) return navigate(-1);
    if (stepPayment === EPaymentStep.DEFERRAL_PAYMENT_REQUEST)
      return setStepPayment(EPaymentStep.CHOOSE_METHOD);
    if (stepPayment > 1 && !isPayForGift)
      return setStepPayment((prev) => prev - 1);
    return navigate(
      routes.CONVERSATION.replace(':id', location.state?.channelId || ''),
      {
        state: {
          isFinish: true,
        },
      },
    );
  }, [
    data?.isPaymentRequest,
    isPayForGift,
    location.state?.channelId,
    navigate,
    stepPayment,
  ]);

  const handleSubmitMethod = useCallback(
    async (method: EPaymentMethod) => {
      if ([EPaymentMethod.CARD, EPaymentMethod.VISA].includes(method)) {
        await handlePayment();
        setUserRefreshRequested(true);
      } else if (
        [EPaymentMethod.POSTPAY, EPaymentMethod.BANK].includes(method)
      ) {
        setStepPayment(EPaymentStep.DEFERRAL_PAYMENT_REQUEST);
      } else {
        setStepPayment(EPaymentStep.ENTER_AMOUNT);
      }
    },
    [handlePayment, setUserRefreshRequested],
  );

  // Effects
  useEffect(() => {
    if (data?.isPaymentRequest || isPayForGift) {
      handleSetStepPayment();
    }
  }, [data?.isPaymentRequest, isPayForGift, handleSetStepPayment]);

  return (
    <div className="h-full overflow-y-auto">
      <Header
        title={
          <Text bold className="text-sm">
            {TEXT_STRING.CHAT.RELEASE_TECO_GIRL}
          </Text>
        }
        onBack={handleBack}
        disabled={isPending}
      />
      {data ? (
        <PageWrapper loading={isLoading} height="h-auto" className="p-6">
          {isDeferralPayment ? (
            <DeferralPaymentRequest
              paymentPendingData={data}
              onChangeAmount={handleChangeAmount}
              amount={amountPayment}
              remainingAmount={+data.amount}
              type={deferralType}
            />
          ) : (
            <>
              <Text bold center className="text-lg mb-4">
                {data.isPaymentRequest ? paymentStr.ADVANCE_PAYMENT : ''}
              </Text>
              <Text bold center className="text-sm">
                {stepPayment === EPaymentStep.ENTER_AMOUNT
                  ? paymentStr.ENTER_AMOUNT
                  : paymentStr.CHOOSE_PAYMENT_METHOD_PLEASE}
              </Text>
              <Text bold center className="text-3xl">
                {stepPayment === EPaymentStep.PAYMENT
                  ? ''
                  : getFormattedAmount(totalAmount, commonStr.YEN)}
              </Text>
          <GachaTicketDiscount payment={data} />
              {stepPayment === EPaymentStep.CHOOSE_METHOD && (
                <MethodPayment
                  onChooseMethod={(method) => setPaymentMethod(method)}
                  paymentMethod={paymentMethod}
                  onSubmit={() => handleSubmitMethod(paymentMethod)}
                  price={+data.amount}
                  loading={isPending || isRefetching}
                  allowPaymentMethod={
                    isPayForGift ? [EPaymentMethod.CARD] : allowPaymentMethod
                  }
                />
              )}
              {stepPayment === EPaymentStep.ENTER_AMOUNT && (
                <PaymentAmount
                  paymentMethod={paymentMethod}
                  className="mt-8"
                  onPayment={handleMethodPayment}
                  loading={isPending}
                  onChangeAmount={handleChangeAmount}
                  amount={amountPayment}
                  remainingAmount={+data.amount}
                />
              )}
              {stepPayment === EPaymentStep.PAYMENT && (
                <CardPayment
                  title={''}
                  price={amountPayment + amountPayment * +data.fee}
                  onPay={handlePayment}
                  paying={isPending}
                />
              )}
            </>
          )}
          <ModalPaymentSuccess
            open={open}
            paymentMethod={paymentMethod}
            onConfirm={onConfirmSuccess}
            amount={resPayment?.amount || 0}
            fee={data.isPaymentRequest ? data.fee : undefined}
            text={resPayment?.message}
          />
        </PageWrapper>
      ) : (
        isError && (
          <Text center className="py-10" textColor="text-error">
            {error?.message || TEXT_STRING.COMMON.SOMETHING_WENT_WRONG}
          </Text>
        )
      )}
    </div>
  );
};

export default Payment;
